import { Injectable } from '@angular/core';

declare let OffscreenCanvas: any;

@Injectable({
  providedIn: 'root',
})
export class GpuDetectorService {
  private _hasMajorPerformanceCaveat: boolean | null = null;
  private _supportsFloatTextures: boolean | null = null;

  // GPUs known to fail with MediaPipe's GPU-delegated segmentation in WASM workers,
  // even when the main-thread WebGL2 context reports float texture support.
  //
  // Sources:
  // - https://github.com/gpuweb/gpuweb/issues/4417 (WebGPU float texture compat telemetry)
  // - https://github.com/google-ai-edge/mediapipe/issues/5190 (segmenter fails on Android)
  // - https://github.com/google/mediapipe/issues/2332 (Mali-G51 incorrect inference)
  // - Chromium gpu_driver_bug_list.json entry 358 (force_enable_color_buffer_float)
  //
  // Midgard-era Mali: no EXT_color_buffer_float at all or only partial support
  // Bifrost 1st-gen Mali (G71, G51): claim support but fail inside WASM WebGL contexts
  // Old Adreno 3xx/4xx: ES 3.0 only or FRAMEBUFFER_INCOMPLETE_ATTACHMENT with float FBOs
  // PowerVR SGX: ES 2.0 only, no WebGL2
  private static readonly BLOCKED_GPU_RENDERERS = [
    // Mali Midgard (no float FBO support or only partial)
    'Mali-T604',
    'Mali-T624',
    'Mali-T628',
    'Mali-T720',
    'Mali-T760',
    'Mali-T820',
    'Mali-T830',
    'Mali-T860',
    'Mali-T880',
    // Mali Bifrost 1st gen (claim support, fail in WASM worker context)
    'Mali-G51',
    'Mali-G71',
    // Adreno 3xx (ES 3.0 only, no EXT_color_buffer_float)
    'Adreno (TM) 302',
    'Adreno (TM) 304',
    'Adreno (TM) 305',
    'Adreno (TM) 306',
    'Adreno (TM) 308',
    'Adreno (TM) 320',
    'Adreno (TM) 330',
    // Adreno 4xx (documented float FBO bugs)
    'Adreno (TM) 405',
    'Adreno (TM) 418',
    'Adreno (TM) 420',
    'Adreno (TM) 430',
    // PowerVR SGX (ES 2.0 only)
    'PowerVR SGX',
  ];

  /**
   * Gets a boolean indicating if the engine can be instantiated on a performant device (ie. if a webGL context can be found and it does not use a slow implementation)
   * ref: https://github.com/BabylonJS/Babylon.js/blob/master/packages/dev/core/src/Engines/thinEngine.ts#L6092
   */
  hasMajorPerformanceCaveat(): boolean {
    if (this._hasMajorPerformanceCaveat === null) {
      try {
        const tempcanvas = document.createElement('canvas') as any;
        tempcanvas.width = 1;
        tempcanvas.height = 1;
        const gl =
          tempcanvas.getContext('webgl', { failIfMajorPerformanceCaveat: true }) ||
          tempcanvas.getContext('experimental-webgl', {
            failIfMajorPerformanceCaveat: true,
          });

        this._hasMajorPerformanceCaveat = !gl;
      } catch (e) {
        this._hasMajorPerformanceCaveat = true;
      }
    }

    return this._hasMajorPerformanceCaveat;
  }

  /**
   * Checks whether the GPU supports 4-channel float32 or float16 textures (renderable).
   * MediaPipe's segmentation postprocessor requires this for GPU-delegated inference.
   *
   * Uses a blocklist of known-bad GPU renderers combined with an extension presence
   * check. A runtime render test is insufficient here because some GPUs (e.g. Mali-G71)
   * pass main-thread WebGL2 float texture tests but fail inside MediaPipe's
   * WASM-created WebGL context in the worker.
   */
  supportsFloatTextures(): boolean {
    if (this._supportsFloatTextures === null) {
      this._supportsFloatTextures = false;
      try {
        const canvas = new OffscreenCanvas(1, 1);
        const gl = canvas.getContext('webgl2') as WebGL2RenderingContext | null;
        if (!gl) {
          return false;
        }

        // Check GPU renderer against known-bad list
        const renderer = this.getGpuRenderer(gl);
        if (
          renderer &&
          GpuDetectorService.BLOCKED_GPU_RENDERERS.some((blocked) => renderer.includes(blocked))
        ) {
          gl.getExtension('WEBGL_lose_context')?.loseContext();
          return false;
        }

        // Extensions must actually be obtained — null means the GPU doesn't
        // advertise float rendering at all.
        const hasF32Ext = !!gl.getExtension('EXT_color_buffer_float');
        const hasF16Ext = !!gl.getExtension('EXT_color_buffer_half_float');

        this._supportsFloatTextures = hasF32Ext || hasF16Ext;

        gl.getExtension('WEBGL_lose_context')?.loseContext();
      } catch (e) {
        this._supportsFloatTextures = false;
      }
    }

    return this._supportsFloatTextures;
  }

  private getGpuRenderer(gl: WebGL2RenderingContext): string | null {
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    if (!ext) {return null;}
    return gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) as string;
  }
}
